import { api } from '../api.ts';
import { app, loadCapabilities } from '../state/app.ts';
import { voiceCapability } from './capability.ts';
import { VoiceError, type VoiceErrorKind } from './errors.ts';

// Spraakherkenning in de browser met ElevenLabs Scribe v2 Realtime: microfoon → PCM16 16 kHz →
// websocket met een kortlevend token van de server. Deelresultaten live, VAD sluit een segment af.
// Fouten gaan als Error door (getUserMedia ongewijzigd, de rest als VoiceError); describeVoiceError
// in errors.ts maakt er een zin voor Jeroen van.

export interface ScribeSession {
  stop(): void;
  /** Sluit het huidige segment handmatig af. */
  commit(): void;
}

export interface ScribeOptions {
  onPartial: (text: string) => void;
  onCommitted: (text: string) => void;
  /** Een fout tijdens de sessie, hooguit één keer; de sessie (en de microfoon) is dan al gestopt. */
  onError: (error: Error) => void;
  onLevel?: ((level: number) => void) | undefined;
  vocabulary?: string[] | undefined;
  silenceSeconds?: number | undefined;
}

const WORKLET = `
class JeromePcm extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.n = 0; this.ratio = sampleRate / 16000; this.acc = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    let level = 0;
    for (let i = 0; i < ch.length; i++) {
      level = Math.max(level, Math.abs(ch[i]));
      this.acc += 1;
      if (this.acc >= this.ratio) {
        this.acc -= this.ratio;
        const s = Math.max(-1, Math.min(1, ch[i]));
        this.buf.push(s < 0 ? s * 0x8000 : s * 0x7fff);
      }
    }
    if (this.buf.length >= 4096) {
      const out = new Int16Array(this.buf.splice(0, 4096));
      this.port.postMessage({ pcm: out.buffer, level }, [out.buffer]);
    }
    return true;
  }
}
registerProcessor('jerome-pcm', JeromePcm);`;

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

// Het Scribe-token is eenmalig en een kwartier geldig; het ophalen kost soms seconden. Daarom ligt er
// steeds één klaar (opgehaald bij het openen van Mail en na elk gebruik), zodat de microfoon direct start.
export const TOKEN_FRESH_MS = 12 * 60_000;
let readyToken: { token: string; at: number } | null = null;
let fetchingToken: Promise<string | null> | null = null;
/** Het token dat onderweg is, is al aan een aanroeper beloofd (elk token is maar één keer te gebruiken). */
let fetchingClaimed = false;

async function fetchToken(): Promise<string> {
  const { token } = await api<{ token: string }>('/api/voice/token', { method: 'POST' });
  return token;
}

/** Legt op de achtergrond een token klaar voor de volgende keer dat de microfoon start; niet als spraak uit staat. */
export function primeScribeToken(): void {
  const caps = app.get().capabilities;
  if (!caps) {
    // Nog niet bekend of de server een ElevenLabs-sleutel heeft: eerst vragen (gedeeld verzoek), dan pas klaarleggen.
    void loadCapabilities().then((loaded) => {
      if (loaded && voiceCapability(loaded).dictation) primeScribeToken();
    });
    return;
  }
  if (!voiceCapability(caps).dictation) return;
  if ((readyToken && Date.now() - readyToken.at < TOKEN_FRESH_MS) || fetchingToken) return;
  fetchingClaimed = false;
  fetchingToken = fetchToken()
    .then((token) => {
      if (!fetchingClaimed) readyToken = { token, at: Date.now() };
      return token;
    })
    .catch(() => null)
    .finally(() => {
      fetchingToken = null;
    });
}

/**
 * Het klaarliggende token, of het token dat onderweg is als niemand anders daar al op wacht; anders nu zelf ophalen.
 * Legt daarna een nieuwe klaar. Twee microfoons tegelijk (overlay en balk) krijgen zo nooit hetzelfde eenmalige token.
 */
export async function takeScribeToken(): Promise<string> {
  const fresh = readyToken && Date.now() - readyToken.at < TOKEN_FRESH_MS ? readyToken.token : null;
  readyToken = null;
  if (fresh) {
    primeScribeToken();
    return fresh;
  }
  if (fetchingToken && !fetchingClaimed) {
    fetchingClaimed = true;
    const token = await fetchingToken;
    if (token) {
      primeScribeToken();
      return token;
    }
  }
  const token = await fetchToken();
  primeScribeToken();
  return token;
}

/** Voor tests: alles vergeten. */
export function resetScribeTokens(): void {
  readyToken = null;
  fetchingToken = null;
  fetchingClaimed = false;
}

const offline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/** Soort fout bij een foutbericht van Scribe (message_type zoals scribe_auth_error of scribe_quota_exceeded_error). */
export function scribeErrorKind(type: string, text: string): VoiceErrorKind {
  const s = `${type} ${text}`.toLowerCase();
  if (/quota|credit|unaccepted|payment|insufficient_funds/.test(s)) return 'rejected';
  if (/auth|token|unauthori[sz]ed|expired|time_limit|insufficient_audio/.test(s)) return 'expired';
  if (/throttl|rate.?limit|resource_exhausted|queue_overflow|concurren|overload/.test(s)) return 'busy';
  return 'service';
}

/** Soort fout bij een websocket die sluit terwijl er nog geluisterd wordt: ook een gewone sluiting (1000) betekent dat de sessie voorbij is. */
export function scribeCloseKind(code: number): VoiceErrorKind {
  if (offline()) return 'offline';
  if (code === 1000 || code === 1001 || code === 1008) return 'expired';
  if (code === 1013) return 'busy';
  return 'service';
}

/** Scribe neemt hooguit 50 termen aan van elk hooguit 20 tekens. */
export const KEYTERM_MAX = 50;
export const KEYTERM_CHARS = 20;

/** De woordenlijst zoals Scribe die aanneemt: zonder lege of dubbele termen; een te lange term ("Hero Interim Professionals") gaat per woord mee. */
export function keyterms(vocabulary: string[]): string[] {
  const out: string[] = [];
  for (const raw of vocabulary) {
    const term = raw.trim().replace(/\s+/g, ' ');
    if (!term) continue;
    const parts = term.length <= KEYTERM_CHARS ? [term] : term.split(' ').filter((w) => w.length <= KEYTERM_CHARS);
    for (const p of parts) if (!out.includes(p)) out.push(p);
  }
  return out.slice(0, KEYTERM_MAX);
}

/** Zet een AudioContext aan het lopen: op iOS blijft hij anders stil (suspended) als hij buiten een tik is gemaakt. */
function resumeQuietly(ctx: AudioContext): void {
  try {
    void Promise.resolve(ctx.resume()).catch(() => undefined);
  } catch {
    // geen resume in deze omgeving
  }
}

export async function startScribe(opts: ScribeOptions): Promise<ScribeSession> {
  const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!media?.getUserMedia) throw new VoiceError('unsupported', 'navigator.mediaDevices ontbreekt');
  // De AudioContext meteen, vóór het eerste await: zo valt hij nog binnen de tik van Jeroen als die er was.
  const ctx = typeof AudioContext === 'undefined' ? null : new AudioContext();
  if (ctx) resumeQuietly(ctx);
  // Token en microfoon tegelijk: het een hoeft niet op het ander te wachten. Wel allebei afwachten:
  // een microfoonfout mag geen onafgevangen tokenfout achterlaten, en bij een tokenfout gaat de
  // microfoon weer uit. De fout van getUserMedia (NotAllowedError, NotFoundError …) gaat ongewijzigd door.
  const [tokenResult, streamResult] = await Promise.allSettled([takeScribeToken(), media.getUserMedia({ audio: { channelCount: 1, sampleRate: { ideal: 16000 }, echoCancellation: true, noiseSuppression: true } })]);
  if (streamResult.status === 'rejected') {
    void ctx?.close().catch(() => undefined);
    throw streamResult.reason instanceof Error ? streamResult.reason : new Error(String(streamResult.reason));
  }
  const stream = streamResult.value;
  const stopTracks = () => {
    for (const t of stream.getTracks()) t.stop();
  };
  if (tokenResult.status === 'rejected') {
    stopTracks();
    void ctx?.close().catch(() => undefined);
    throw tokenResult.reason instanceof Error ? tokenResult.reason : new Error(String(tokenResult.reason));
  }
  const token = tokenResult.value;
  if (!ctx || typeof AudioWorkletNode === 'undefined') {
    stopTracks();
    void ctx?.close().catch(() => undefined);
    throw new VoiceError('unsupported', 'AudioContext of AudioWorklet ontbreekt');
  }
  let source: MediaStreamAudioSourceNode;
  let node: AudioWorkletNode;
  try {
    const workletUrl = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
    try {
      await ctx.audioWorklet.addModule(workletUrl);
    } finally {
      URL.revokeObjectURL(workletUrl);
    }
    source = ctx.createMediaStreamSource(stream);
    node = new AudioWorkletNode(ctx, 'jerome-pcm');
    source.connect(node);
  } catch (e) {
    // Zonder deze opruiming bleef de microfoon aan (de rode stip van de browser) als het audiodeel niet startte.
    stopTracks();
    void ctx.close().catch(() => undefined);
    // Een nieuwe Error: een AbortError van de worklet is geen bezette microfoon.
    throw new Error(`Audio starten mislukt: ${e instanceof Error ? e.message : String(e)}`);
  }
  const audio = ctx;

  const params = new URLSearchParams({ model_id: 'scribe_v2_realtime', token, audio_format: 'pcm_16000', language_code: 'nl', commit_strategy: 'vad', vad_silence_threshold_secs: String(opts.silenceSeconds ?? 1.2) });
  // De woordenlijst van Jeroen (namen, vaktermen): Scribe neigt dan naar die woorden. Herhaalde parameter, zoals de dienst wil.
  for (const term of keyterms(opts.vocabulary ?? [])) params.append('keyterms', term);
  const ws = new WebSocket(`wss://api.elevenlabs.io/v1/speech-to-text/realtime?${params.toString()}`);
  let open = false;
  let stopped = false;
  // Geluid van vóór het openen van de verbinding (de eerste woorden, terwijl de websocket nog opzet) wacht
  // in deze buffer en gaat direct na het openen weg, in volgorde; hooguit zo'n tien seconden.
  const pending: string[] = [];
  const PENDING_MAX = 40;
  const send = (chunk: string, commit: boolean) => {
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ message_type: 'input_audio_chunk', audio_base_64: chunk, commit, sample_rate: 16000 }));
  };
  // Eén fout per sessie: de sessie stopt meteen (microfoon uit), zodat wat erna komt (onerror gevolgd door onclose) zwijgt.
  const fail = (error: VoiceError) => {
    if (stopped) return;
    stop();
    opts.onError(error);
  };
  node.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; level: number }>) => {
    opts.onLevel?.(e.data.level);
    const chunk = toBase64(e.data.pcm);
    if (open) send(chunk, false);
    else if (pending.length < PENDING_MAX) pending.push(chunk);
  };
  ws.onopen = () => {
    open = true;
    for (const chunk of pending.splice(0)) send(chunk, false);
  };
  ws.onmessage = (m: MessageEvent) => {
    let msg: { message_type?: string; text?: string; error?: string; message?: string };
    try {
      msg = JSON.parse(String(m.data)) as typeof msg;
    } catch {
      return; // onleesbaar
    }
    const type = msg.message_type ?? '';
    if (type === 'partial_transcript' && msg.text) opts.onPartial(msg.text);
    else if (type === 'committed_transcript' && msg.text?.trim()) opts.onCommitted(msg.text.trim());
    else if (/error/i.test(type) || msg.error) {
      const detail = msg.error ?? msg.message ?? '';
      fail(new VoiceError(scribeErrorKind(type, detail), `Scribe: ${type || 'fout'} ${detail}`.trim()));
    }
  };
  ws.onerror = () => fail(new VoiceError(offline() ? 'offline' : 'service', 'Scribe: de websocket gaf een fout'));
  ws.onclose = (e: CloseEvent) => {
    open = false;
    fail(new VoiceError(scribeCloseKind(e.code), `Scribe gesloten (${e.code}${e.reason ? `: ${e.reason}` : ''})`));
  };

  function stop(): void {
    if (stopped) return;
    stopped = true;
    try {
      node.port.onmessage = null;
      source.disconnect();
      node.disconnect();
    } catch {
      // al los
    }
    stopTracks();
    void audio.close().catch(() => undefined);
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000);
  }
  return { stop, commit: () => send('', true) };
}
